import axios from "axios";
import { store } from "../store/index";
import {
  clearCredentials,
  updateAccessToken,
  selectToken,
  selectRefreshToken,
} from "../store/authSlice";

const BASE_URL = import.meta.env.VITE_BASE_URL;
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

export function setupAxiosInterceptors() {
  // Attach current access token to every request
  axios.interceptors.request.use(
    (config) => {
      const token = selectToken(store.getState());
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // On 401 → try silent refresh once, then auto-logout
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      // Skip refresh for the refresh endpoint itself to avoid infinite loops
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/refresh-token")
      ) {
        const refreshToken = selectRefreshToken(store.getState());

        if (!refreshToken) {
          store.dispatch(clearCredentials());
          window.location.href = "/login?session=expired";
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Queue requests that arrive while refresh is in-flight
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post(
            `${BASE_URL}/api/telefect/v1/auth/refresh-token`,
            { refreshToken }
          );
          const newToken = res.data.token;
          store.dispatch(updateAccessToken({ token: newToken }));
          processQueue(null, newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          store.dispatch(clearCredentials());
          window.location.href = "/login?session=expired";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}
